<template>
  <div>
    <page-header
      :title="title"
      :container-class="containerClass"
      :info-text="'Select date range, program and school to fetch respective analytics'"
    />
    <div :class="containerClass"></div>
    <div class="page-section border-bottom-2">
      <div :class="containerClass">
        <div class="mt-3">
          <div>
            <div class="row">
              <div class="col">
                <div class="mb-3">
                  <h5>Filter by Date</h5>
                </div>

                <div>
                  <date-picker
                    v-model="filters.dateRange"
                    format="MMMM DD, YYYY"
                    valueType="YYYY-MM-DD"
                    style="width: 100%"
                    id="date"
                    lang="en"
                    placeholder="Filter by Start Date - End Date Range"
                    range
                    class="w-100"
                    :class="isDateEmpty || dateLimitExceeded ? 'mb-1' : 'mb-3'"
                    :input-attr="{ required: 'true' }"
                  ></date-picker>
                  <b-form-invalid-feedback class="mt-0 mb-2" :state="isDateEmpty ? false : null"
                    >This field is required.</b-form-invalid-feedback
                  >
                  <b-form-invalid-feedback class="mt-0 mb-2" :state="dateLimitExceeded ? false : null"
                    >Please select a date range of one year or less.</b-form-invalid-feedback
                  >
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <h5>Filter by Program</h5>
                </div>
                <div>
                  <v-select
                    id="programs"
                    class="form-control v-select-custom"
                    label="title"
                    v-model="filters.program"
                    :reduce="item => item.id"
                    placeholder="All Programs"
                    :options="allPrograms"
                  >
                    <template #search="{ attributes, events }">
                      <input class="vs__search" :required="!filters.program" v-bind="attributes" v-on="events" />
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-flex align-items-center">
                        <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                          <b-img
                            :src="option.image_url"
                            class="img-fluid"
                            width="20"
                            alt="Logo"
                            v-if="option.image_url"
                          />
                          <md-icon v-else>local_library</md-icon>
                        </fmv-avatar>
                        <span>{{ option.title }}</span>
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3" :class="isMobileScreen ? 'mt-3' : ''">
                  <h5>Filter by School</h5>
                </div>

                <div>
                  <v-select
                    id="schools"
                    class="form-control v-select-custom"
                    label="title"
                    v-model="filters.school"
                    :reduce="item => item.id"
                    placeholder="All Schools"
                    :options="allSchools"
                  >
                    <template #search="{ attributes, events }">
                      <input class="vs__search" :required="!filters.school" v-bind="attributes" v-on="events" />
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-flex align-items-center">
                        <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                          <b-img :src="option.image" class="img-fluid" width="20" alt="Logo" v-if="option.image" />
                          <md-icon v-else>local_library</md-icon>
                        </fmv-avatar>
                        <span>{{ option.title }}</span>
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <page-separator>
          <b-link v-b-toggle="'registeration-over-time-section'" class="accordion__toggle p-0">
            <span class="flex">Participants Over Time</span>
            <md-icon class="accordion__toggle-icon">
              {{ participantOverTimeVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </md-icon>
          </b-link>
        </page-separator>
        <b-collapse
          id="registeration-over-time-section"
          accordion="registerOverTimeAccordionId"
          :visible="true"
          class="accordion__menu"
          @hide="participantOverTimeVisible = false"
          @show="participantOverTimeVisible = true"
        >
          <div class="card border-left-0 mt-2">
            <div class="card-body">
              <div class="mb-1">
                <div class="flex">
                  <div class="row">
                    <div class="col-lg-auto d-flex align-items-center">
                      <h5 class="card-title fw800" style="font-weight: bold;">Participants Over Time</h5>
                    </div>
                    <div class="col-lg d-flex mb-2 flex-wrap justify-content-end align-items-center">
                      <!-- <div class="d-flex flex-wrap"> -->
                      <v-select
                        :hidden="isMobileScreen"
                        id="time-frame"
                        class="form-control v-select-custom mr-2"
                        label="label"
                        v-model="timeFrame"
                        :reduce="item => item.value"
                        placeholder="Select Time Frame"
                        :options="timeFrameOptions"
                        :clearable="false"
                        :style="`width:120px`"
                      >
                        <template #search="{ attributes, events }">
                          <input class="vs__search" :required="!timeFrame" v-bind="attributes" v-on="events" />
                        </template>
                        <template slot="option" slot-scope="option">
                          <div class="d-flex align-items-center">
                            <span>{{ option.label }}</span>
                          </div>
                        </template>
                      </v-select>
                      <!-- </div> -->
                      <b-btn
                        :hidden="isMobileScreen"
                        variant="primary"
                        :disabled="isParticipantOverTimeRosterLoading"
                        @click.prevent="fetchParticipantsOverTimeRoster"
                        :style="`margin-top:${isMobileScreen ? '10px' : '0'}`"
                      >
                        Export to Excel
                        <i v-if="isParticipantOverTimeRosterLoading" class="fas fa-circle-notch fa-spin ml-1"></i>
                        <span v-else class="material-icons icon-16pt ml-1">file_download</span>
                      </b-btn>
                    </div>
                  </div>
                  <p class="text-70">
                    View and export the total participants in selected program and school. Use the date filter above to
                    narrow down your selection based on organization sign up date.
                  </p>
                  <div v-if="isMobileScreen" class="timeframe-select">
                    <v-select
                      id="time-frame"
                      class="form-control v-select-custom w-65 "
                      label="label"
                      v-model="timeFrame"
                      :reduce="item => item.value"
                      placeholder="Select Time Frame"
                      :options="timeFrameOptions"
                      :clearable="false"
                    >
                      <template #search="{ attributes, events }">
                        <input class="vs__search" :required="!timeFrame" v-bind="attributes" v-on="events" />
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-flex align-items-center">
                          <span>{{ option.label }}</span>
                        </div>
                      </template>
                    </v-select>

                    <b-btn
                      variant="primary"
                      :disabled="isParticipantOverTimeRosterLoading"
                      @click.prevent="fetchParticipantsOverTimeRoster"
                      style="width: 100%"
                      :style="`margin-top:${isMobileScreen ? '10px' : '0'}`"
                      class="btn-normal mb-2"
                    >
                      Export to Excel
                      <i v-if="isParticipantOverTimeRosterLoading" class="fas fa-circle-notch fa-spin ml-1"></i
                      ><span v-else class="material-icons icon-16pt ml-1">file_download</span>
                    </b-btn>
                  </div>

                  <div v-else>
                    <div class="d-flex mb-2" style="justify-content: space-between;"></div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <div class="chart-container mt-4" style="position: relative; ">
                        <div v-if="isChartLoading">
                          <div class="text-center mt-5 mb-4">
                            <b-spinner
                              class="align-middle mr-2"
                              :style="{ color: getLoggedInUser.linked_entity.primary_color }"
                            ></b-spinner>

                            <strong>Loading...</strong>
                          </div>
                        </div>
                        <canvas v-else id="registration-graph" class="chart-container"></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
        <page-separator>
          <b-link v-b-toggle="'general-report-section'" class="accordion__toggle p-0">
            <span class="flex">General Reporting</span>
            <md-icon class="accordion__toggle-icon">
              {{ generalReportVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </md-icon>
          </b-link>
        </page-separator>
        <b-collapse
          id="general-report-section"
          accordion="generalAccordionId"
          :visible="false"
          class="accordion__menu"
          @hide="generalReportVisible = false"
          @show="generalReportVisible = true"
        >
          <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
            <div class="card border-left-0">
              <div class="card-body">
                <div class="d-flex mb-1">
                  <div class="flex">
                    <div class="d-flex align-items-center">
                      <h5 class="card-title fw800" style="font-weight: bold;">
                        Export General Report
                      </h5>
                    </div>
                    <p class="text-70 mt-4 mb-0">
                      Export the list of associated participants along with their details enrolled in selected program
                      and school. Use the date filter above to narrow down your selection based on organization sign up
                      date.
                    </p>
                    <page-separator
                      title="Background Related Information
          "
                    />

                    <div class="row">
                      <div
                        v-for="(item, index) in background_info"
                        :key="index"
                        class="col-lg-6 col-md-6 col-sm-12 clickable-item"
                      >
                        <div class="mt-4 mb-0 p-3 my-auto d-flex justify-content-start">
                          <b-form-checkbox v-model="apiData[item]">
                            <div class="d-flex">
                              <span style="font-size: 16px">{{ $t(`participantAnalytics.${item}`) }}</span>
                            </div>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </div>

                    <page-separator
                      title="Career Related Information
          "
                    />

                    <div class="row">
                      <div
                        v-for="(item, index) in career_info"
                        :key="index"
                        class="col-lg-6 col-md-6 col-sm-12 clickable-item"
                      >
                        <div class="mt-4 mb-0 p-3 my-auto d-flex justify-content-start">
                          <b-form-checkbox v-model="apiData[item]">
                            <div class="d-flex">
                              <span style="font-size: 16px">{{ $t(`participantAnalytics.${item}`) }}</span>
                            </div>
                          </b-form-checkbox>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex justify-content-end">
                      <b-btn variant="primary" style="width: 200px" type="submit" class="btn-normal mb-2">
                        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
                        <span v-else
                          >Export Reporting<span class="material-icons icon-16pt ml-1">file_download</span></span
                        >
                      </b-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-form>
        </b-collapse>
        <page-separator>
          <b-link v-b-toggle="'registeration-roster-section'" class="accordion__toggle p-0">
            <span class="flex">Associated Participant Roster</span>
            <md-icon class="accordion__toggle-icon">
              {{ registerRosterVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
            </md-icon>
          </b-link>
        </page-separator>
        <b-collapse
          id="registeration-roster-section"
          accordion="registrationRosterAccordionId"
          :visible="false"
          class="accordion__menu"
          @hide="registerRosterVisible = false"
          @show="registerRosterVisible = true"
        >
          <div class="card border-left-0 mt-2">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-3">
                    <h5 class="card-title fw800" style="font-weight: bold;">Export Associated Participant Roster</h5>
                  </div>
                  <p class="text-70 mb-2">
                    View and export the list of associated participants enrolled in selected program and school. Use the
                    date filter above to narrow down your selection based on organization sign up date.
                  </p>
                  <div class="d-flex justify-content-end mt-3">
                    <b-btn
                      variant="primary"
                      :disabled="isExportLoading"
                      @click.prevent="fetchParticipantRosters"
                      style="width: 200px"
                      class="btn-normal"
                    >
                      Export Roster <i v-if="isExportLoading" class="fas fa-circle-notch fa-spin ml-1"></i
                      ><span v-else class="material-icons icon-16pt ml-1">file_download</span>
                    </b-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
    <div>
      <!-- Your modal trigger button goes here -->
    </div>
    <b-modal ref="myModal" title="Downloading Report" centered hide-footer @hide="hideModal">
      <span v-if="!fileUrl">
        <i
          style="font-size: 3rem"
          class="d-flex justify-content-center fas fa-circle-notch fa-spin text-primary mb-2"
        ></i>
        <span>Your file is being downloaded, we will notify you once it’s ready.</span>
        <p class="text-70 text-center">
          Note: You can safely move to another page. You will receive a notification shortly.
        </p>
      </span>
      <span v-else>
        <i
          style="font-size: 3rem"
          class="d-flex justify-content-center material-icons text-primary mb-2 clickable-item"
          @click.prevent="downloadGivenFile"
          >file_download</i
        >
        <span>Your file is ready to be downloaded. Click to download.</span>
      </span>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';
import vSelect from 'vue-select';
import Page from '@/components/Page.vue';
import { formatToAPIDate } from '../../../common/utils';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { FmvAvatar } from 'fmv-avatar';
import { Chart } from 'chart.js';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: { DatePicker, vSelect, PageHeader, PageSeparator, FmvAvatar },
  extends: Page,

  data() {
    return {
      title: 'Participant Reporting',
      background_info: ['gender', 'age', 'language', 'ethnicity'],
      career_info: ['work_type', 'facility_interest'],
      areProgramsLoading: false,
      areSchoolsLoading: false,
      allSchools: [],
      isExportLoading: false,
      isLoading: false,
      generalReportVisible: false,
      registerRosterVisible: false,
      participantOverTimeVisible: true,
      connection: null,
      apiData: {
        gender: true,
        age: true,
        language: true,
        ethnicity: true,
        work_type: true,
        facility_interest: true,
      },
      filters: {
        dateRange: [],
        program: null,
        school: null,
      },
      allPrograms: [],
      bis: [],
      fileUrl: null,
      timeFrame: 'weekly',
      timeFrameOptions: [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],
      isParticipantOverTimeRosterLoading: false,
      chartData: {
        type: 'bar',
        data: {},
        options: this.chartOptions,
      },
      chartInstance: null,
      isChartLoading: false,
      windowWidth: window.innerWidth,
      isDateEmpty: false,
      dateLimitExceeded: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getAuthToken']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Dashboard', active: true },
      ];
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        indexAxis: 'x',
        plugins: {
          legend: {
            position: 'top',
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      };
    },
  },

  methods: {
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('student', [
      'getOrgParticipantsOverTimeRoster',
      'getOrgParticipantsOverTimeGraph',
      'createOrgParticipantAnalysisReport',
      'createOrgParticipantReport',
    ]),
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('school', ['getAllSchools']),
    get,
    resetFile() {
      this.fileUrl = null;
    },
    openModal() {
      this.$refs.myModal.show();
    },
    hideModal() {
      this.resetFile();
      this.$refs.myModal.hide();
      this.closeConnection();
    },
    validateDate() {
      if (!this.filters.dateRange[0] || !this.filters.dateRange[1]) {
        this.isDateEmpty = true;
        this.dateLimitExceeded = false;
        this.makeToast({ variant: 'danger', msg: 'Please select specific date range' });
        return false;
      } else {
        this.isDateEmpty = false;
        const startDate = new Date(this.filters.dateRange[0]);
        const endDate = new Date(this.filters.dateRange[1]);

        const oneYearInMs = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
        const diff = endDate - startDate;

        if (diff > oneYearInMs) {
          this.makeToast({ variant: 'danger', msg: 'Date range must be 1 year or less' });
          this.dateLimitExceeded = true;
          return false;
        } else {
          return true;
        }
      }
    },
    async downloadGivenFile({ removeTimestamp = true }) {
      const h = this.$createElement;
      this.makeToast({
        variant: 'primary',
        msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'Downloading File']),
        toaster: 'b-toaster-bottom-left',
      });
      try {
        await this.downloadFile({ fileUrl: this.fileUrl, removeTimestamp });

        this.makeToast({
          variant: 'success',
          msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
          toaster: 'b-toaster-bottom-left',
        });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.hideModal();
    },
    hookUrlWS(timestamp) {
      this.connection = new WebSocket(
        `${process.env.VUE_APP_BACKEND_WS_URL}/ws/analytics/${this.getLoggedInUser.id}/${timestamp}/?token=${this.getAuthToken}`,
      );

      this.connection.onmessage = event => {
        const data = JSON.parse(event.data).message;

        if (data.action === 'Create') {
          this.fileUrl = data.file_url;
        }
      };
      this.connection.onerror = () => {};
    },
    closeConnection() {
      if (this.connection) this.connection.close();
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;

      const response = await this.getAllPrograms();
      this.allPrograms = response.data;

      this.areProgramsLoading = false;
    },
    async downloadCSV(url) {
      try {
        await this.downloadFile({
          fileUrl: url,
        });
        const h = this.$createElement;
        this.makeToast({
          variant: 'success',
          msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
          toaster: 'b-toaster-bottom-left',
        });
      } catch (error) {
        //
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchParticipantRosters() {
      if (!this.validateDate()) {
        return;
      }
      this.isDateEmpty = false;
      this.dateLimitExceeded = false;

      this.isExportLoading = true;
      try {
        const response = await this.createOrgParticipantReport({
          school: this.filters.school,
          program_id: this.filters.program,
          date_from: this.filters.dateRange[0],
          date_to: this.filters.dateRange[1],
        });
        this.hookUrlWS(response.data.timestamp);
        this.openModal();
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isExportLoading = false;
    },
    async onSubmit() {
      if (!this.validateDate()) {
        return;
      }
      this.isDateEmpty = false;
      this.dateLimitExceeded = false;

      this.isLoading = true;
      try {
        const data = {
          ...this.apiData,
          program: this.filters.program,
          school: this.filters.school ? this.filters.school : null,
          date_from: this.filters.dateRange[0],
          date_to: this.filters.dateRange[1],
        };
        const res = await this.createOrgParticipantAnalysisReport(data);
        this.hookUrlWS(res.data.timestamp);
        this.openModal();
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
    async fetchParticipantsOverTimeRoster() {
      if (!this.validateDate()) {
        return;
      }
      this.isDateEmpty = false;
      this.dateLimitExceeded = false;

      this.isParticipantOverTimeRosterLoading = true;
      const response = await this.getOrgParticipantsOverTimeRoster({
        prog_id: this.filters.program ? this.filters.program : null,
        school_id: this.filters.school ? this.filters.school : null,
        date_from: this.filters.dateRange[0] ? this.filters.dateRange[0] : null,
        date_to: this.filters.dateRange[1] ? this.filters.dateRange[1] : null,
        time_frame: this.timeFrame,
      });
      this.downloadCSV(response.data.url);
      this.isParticipantOverTimeRosterLoading = false;
    },
    async fetchAssociatedParticipantsGraphData() {
      const response = await this.getOrgParticipantsOverTimeGraph({
        prog_id: this.filters.program ? this.filters.program : null,
        school_id: this.filters.school ? this.filters.school : null,
        date_from: this.filters.dateRange[0] ? this.filters.dateRange[0] : null,
        date_to: this.filters.dateRange[1] ? this.filters.dateRange[1] : null,
        time_frame: this.timeFrame,
      });
      return response.data;
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    getDateRange(timeframeData) {
      if (timeframeData.date_from && timeframeData.date_to) {
        return `( ${timeframeData.date_from} - ${timeframeData.date_to} )`;
      }
      return '';
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({});
      this.allSchools = response.data.map(school => ({
        id: school.id,
        title: school.name,
        image: school.small_logo_url,
      }));

      this.areSchoolsLoading = false;
    },
    async fetchChartData() {
      this.isChartLoading = true;
      const timeframeData = await this.fetchAssociatedParticipantsGraphData();
      const program = this.filters.program
        ? this.allPrograms.filter(item => item.id === this.filters.program)[0].title
        : null;
      this.chartData = {
        type: 'bar',
        data: {
          labels: timeframeData.labels,
          datasets: [
            {
              label: !program ? 'All Programs' : `${program}`,
              data: timeframeData.datasets,
              backgroundColor: this.getLoggedInUser.linked_entity.primary_color,
            },
          ],
        },
        options: {
          legend: {
            labels: {
              fontColor: this.getLoggedInUser.linked_entity.secondary_color,
            },
          },
          maintainAspectRatio: false,
          responsive: true,
          lineTension: 1,
          layout: { padding: { right: 25 } },
          scales: {
            yAxes: [
              {
                ticks: {
                  fontColor: this.getLoggedInUser.linked_entity.secondary_color,
                  beginAtZero: true,
                  padding: this.isMobileScreen ? 8 : 15,
                },
              },
            ],
            xAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: this.getDateRange(timeframeData),
                  fontSize: 14,
                  fontStyle: 'italic',
                },
                ticks: {
                  fontColor: this.getLoggedInUser.linked_entity.secondary_color,
                  padding: this.isMobileScreen ? 8 : 15,
                },
              },
            ],
          },
        },
      };
      this.isChartLoading = false;
    },
    fetchAndUpdateChartData() {
      if (!this.validateDate()) {
        return;
      }
      this.isDateEmpty = false;
      this.dateLimitExceeded = false;
      this.fetchChartData()
        .then(() => {
          const ctx = document.getElementById('registration-graph');
          if (this.chartInstance) {
            this.chartInstance.destroy();
          }

          this.chartInstance = new Chart(ctx, this.chartData);
        })
        .catch();
    },
  },
  watch: {
    'filters.program': 'fetchAndUpdateChartData',
    'filters.dateRange': 'fetchAndUpdateChartData',
    'filters.school': 'fetchAndUpdateChartData',
    timeFrame: 'fetchAndUpdateChartData',
  },
  async mounted() {
    this.fetchPrograms();
    this.fetchSchools();
    this.filters.dateRange = [formatToAPIDate(moment().startOf('month')), formatToAPIDate(moment().endOf('month'))];
    this.fetchAndUpdateChartData();
  },
};
</script>
<style scoped>
.chart-container {
  width: 100%;
  height: 40vh;
}
@media only screen and (max-width: 700px) {
  .chart-container {
    width: 100%;
    height: 40vh;
  }
}
@media only screen and (max-width: 425px) {
  .chart-container {
    width: 250px;
    height: 250px;
  }
}
</style>
